import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * MI-336 — a detail panel the reader can size.
 *
 * The panel was a fixed 35% of the window, which is two decisions taken for
 * everyone: how much of the screen a movement deserves, and how much the list
 * gives up to show one. Neither is ours to make — a reader comparing rows
 * wants a sliver, one editing a movement wants room.
 *
 * Width is tracked in **pixels**, not a percentage: dragging an edge is a
 * statement about how wide that panel should be, and a ratio re-applied to a
 * different window turns it into a different panel. The clamp keeps it honest
 * on a window that later shrinks.
 *
 * **The drag never goes through React state.** The width is published as a
 * CSS custom property on the page's container, and the drag writes it
 * straight onto that node; only the finished gesture reaches `setState`.
 * Routing each frame through state instead re-rendered the whole Movements
 * page — all of the list's rows, with their pickers and tooltips — for every
 * pointer event, which measured 330-430ms of blocked main thread *per move*.
 * The panel visibly trailed the cursor. Nothing about the width needs React:
 * it is one number that one element reads.
 *
 * The width is **not** remembered across a close — every fresh open starts
 * at the default (maximum) width. A narrower panel is something the reader
 * asked for to compare against the list in front of them right now, not a
 * standing preference; carrying it into the next movement is what made the
 * panel open at 280px after a session of testing at that width. Shrinking it
 * mid-session and switching between already-open movements is unaffected —
 * only the closed→open transition resets it.
 */

/** Below this the fields have nowhere left to reflow to. */
export const PANEL_MIN_WIDTH = 280;

/**
 * What 35% of a 1440px window used to be — the width this replaces, and now
 * the panel's ceiling as well as its opening width. The panel only narrows
 * from here; it never grows past the space it always reserved, so a reader
 * comparing rows can shrink it out of the way but can't accidentally turn the
 * detail view into the page.
 */
export const PANEL_DEFAULT_WIDTH = 500;

/** What the panel's width reads from, and what a drag writes to. */
export const PANEL_WIDTH_VAR = '--detail-panel-width';

/**
 * @param isOpen Whether the panel is currently mounted. Watched only for its
 *  false→true edge, which is when the width resets to the default.
 * @param options Defaults are the Movements page's panel. Other pages open
 *  their panel at a share of the page instead, so they pass
 *  `defaultWidth: null` — the property is then left unset and the panel's own
 *  CSS fallback applies until the reader drags it. `listMinWidth` caps a drag
 *  at what leaves the list beside it that much room.
 */
export function useResizablePanel(isOpen, {
  defaultWidth = PANEL_DEFAULT_WIDTH,
  minWidth = PANEL_MIN_WIDTH,
  maxWidth = PANEL_DEFAULT_WIDTH,
  listMinWidth,
} = {}) {
  const [width, setWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const frame = useRef(null);
  // The element carrying the custom property. Attach it to whatever contains
  // both the list and the panel — the panel reads the property, the list gets
  // the rest of the row.
  const containerRef = useRef(null);
  // The live value during a drag, which state does not see until it ends.
  // Null while the panel is still at its CSS default and has not been dragged.
  const widthRef = useRef(defaultWidth);

  const clamp = useCallback((next) => {
    const container = containerRef.current?.getBoundingClientRect().width;
    const roomForPanel = listMinWidth != null && container
      ? Math.max(container - listMinWidth, minWidth)
      : Infinity;
    return Math.min(Math.max(Math.round(next), minWidth), maxWidth, roomForPanel);
  }, [minWidth, maxWidth, listMinWidth]);

  const publish = useCallback((next) => {
    widthRef.current = next;
    const node = containerRef.current;
    if (!node) return;
    // Unset hands the width back to the panel's own CSS default.
    if (next == null) node.style.removeProperty(PANEL_WIDTH_VAR);
    else node.style.setProperty(PANEL_WIDTH_VAR, `${next}px`);
  }, []);

  const commit = useCallback((next) => {
    setWidth(next == null ? null : clamp(next));
  }, [clamp]);

  // State → the property. Covers the reset on open, the keyboard steps, the
  // double-click, and the end of a drag; the drag itself publishes directly.
  useLayoutEffect(() => { publish(width); }, [width, publish]);

  // Opening (closed → open) starts fresh at the default width. `isOpen` only
  // flips on this transition — switching between movements while the panel
  // stays mounted never re-runs this, so an in-session drag isn't undone by
  // clicking a different row.
  useEffect(() => {
    if (isOpen) setWidth(defaultWidth);
  }, [isOpen, defaultWidth]);

  /**
   * The handle captures the pointer, so a fast drag that outruns the cursor
   * keeps resizing instead of dropping the gesture over the list.
   *
   * Width is measured from the right edge of the window rather than from a
   * delta, so it cannot drift over a long drag.
   */
  const startResize = useCallback((event) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture?.(event.pointerId);
    setIsResizing(true);

    const onMove = (moveEvent) => {
      // Straight to the DOM, one write per frame. No setState here: see the
      // note at the top of the file — a render per pointer event is what made
      // the panel lag behind the cursor.
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(() => {
        publish(clamp(window.innerWidth - moveEvent.clientX));
      });
    };

    const onUp = (upEvent) => {
      if (frame.current) cancelAnimationFrame(frame.current);
      handle.releasePointerCapture?.(upEvent.pointerId);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      setIsResizing(false);
      // One state update for the whole gesture, so the rest of the page
      // catches up with what the reader already sees.
      commit(window.innerWidth - upEvent.clientX);
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }, [commit, publish, clamp]);

  /** Keyboard: the handle is focusable, so the panel is sizeable without a
   *  pointer. 24px a step, 96px with shift. */
  const onHandleKeyDown = useCallback((event) => {
    const step = event.shiftKey ? 96 : 24;
    // Still at its CSS default, the panel's width is only known by measuring.
    const current = widthRef.current
      ?? event.currentTarget.parentElement?.getBoundingClientRect().width
      ?? minWidth;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      commit(current + step);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      commit(current - step);
    }
  }, [commit, minWidth]);

  /** Double-click restores the default, the way a split pane usually does. */
  const resetWidth = useCallback(() => commit(defaultWidth), [commit, defaultWidth]);

  // While dragging, the cursor stays col-resize everywhere and nothing selects
  // — otherwise crossing the list mid-drag paints a text selection over it.
  useEffect(() => {
    if (!isResizing) return undefined;
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
    };
  }, [isResizing]);

  return { containerRef, isResizing, startResize, onHandleKeyDown, resetWidth };
}
